import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/use-toast';
import CookieBanner from './components/CookieBanner';
import Analytics from './components/Analytics';
import SEOSchemas from './components/SEOSchemas';
import ReformBanner from './components/ReformBanner';
import { initializeGA } from './lib/ga4';
import { initializeGTM } from './lib/gtm';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PricingPage from './pages/PricingPage';
import OnboardingPage from './pages/OnboardingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import CGUPage from './pages/CGUPage';
import ConfidentialitePage from './pages/ConfidentialitePage';
import CookiesPage from './pages/CookiesPage';
import BlogPage from './pages/BlogPage';
import BlogArticlePageSEO from './pages/BlogArticlePageSEO';
import ComparaisonPage from './pages/ComparaisonPage';
import QuiSommesNousPage from './pages/QuiSommesNousPage';
import GuideDownloadPage from './pages/GuideDownloadPage';
import PartenaireComptablePage from './pages/PartenaireComptablePage';
import TutorialPage from './pages/TutorialPage';
import PartnerDashboardPage from './pages/PartnerDashboardPage';
import SimulateurPage from './pages/SimulateurPage';
import OptiTrajetDemo from './pages/OptiTrajetDemo';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import FoldersPage from './pages/dashboard/FoldersPage';
import DemoPage from './pages/DemoPage';

// Landing Pages Google Ads
import LandingFacturation2026 from './pages/landing/LandingFacturation2026';
import LandingFreelance from './pages/landing/LandingFreelance';
import LandingImpayes from './pages/landing/LandingImpayes';

// Outils gratuits
import OutilFactureGratuit from './pages/outils/OutilFactureGratuit';

// Early Adopter
import EarlyAdopterPage from './pages/EarlyAdopterPage';

// AI Copilot + Upgrade Modal (Phase 1 — Feb 2026)
import Copilot from './components/Copilot';
import { UpgradeModalProvider } from './contexts/UpgradeModalContext';

// Programmatic SEO pages (May 2026)
import CityPage from './pages/seo/CityPage';
import SecteurPage from './pages/seo/SecteurPage';
import ComparaisonProgPage from './pages/seo/ComparaisonProgPage';

// Lead magnet — Compliance calculator
import CalculateurConformitePage from './pages/CalculateurConformitePage';

// Pages de comparaison SEO
import SmartFactureVsIndy from './pages/comparaison/SmartFactureVsIndy';
import SmartFactureVsFreebe from './pages/comparaison/SmartFactureVsFreebe';
import SmartFactureVsTiime from './pages/comparaison/SmartFactureVsTiime';

// Pages Solutions
import SolutionFreelance from './pages/solutions/SolutionFreelance';
import SolutionAutoEntrepreneur from './pages/solutions/SolutionAutoEntrepreneur';
import SolutionArtisan from './pages/solutions/SolutionArtisan';
import SolutionPME from './pages/solutions/SolutionPME';

// Expert Features
import SupportPage from './pages/smartfacture/SupportPage';
import TeamPage from './pages/smartfacture/TeamPage';
import JoinTeamPage from './pages/smartfacture/JoinTeamPage';
import InvoiceTemplatesPage from './pages/smartfacture/InvoiceTemplatesPage';
import ClientImportPage from './pages/smartfacture/ClientImportPage';
import PPFPage from './pages/smartfacture/PPFPage';
import QontoIntegrationPage from './pages/smartfacture/QontoIntegrationPage';
import MobileAppPage from './pages/smartfacture/MobileAppPage';

// OptiTrajet Pages
import OptiTrajetLogin from './pages/optitrajet/LoginPage';
import OptiTrajetRegister from './pages/optitrajet/RegisterPage';
import OptiTrajetDashboard from './pages/optitrajet/DashboardPage';
import OptiTrajetTrips from './pages/optitrajet/TripsPage';
import OptiTrajetNewTrip from './pages/optitrajet/NewTripPage';
import OptiTrajetExpenses from './pages/optitrajet/ExpensesPage';
import OptiTrajetNewExpense from './pages/optitrajet/NewExpensePage';
import OptiTrajetScanExpense from './pages/optitrajet/ScanExpensePage';
import OptiTrajetVehicles from './pages/optitrajet/VehiclesPage';
import OptiTrajetReports from './pages/optitrajet/ReportsPage';
import OptiTrajetSettings from './pages/optitrajet/SettingsPage';

import SharedInvoicePage from './pages/SharedInvoicePage';
import DashboardLayout from './pages/DashboardLayout';
import OverviewPage from './pages/OverviewPage';
import SmartFactureDashboard from './pages/smartfacture/SmartFactureDashboard';
import CreateInvoicePage from './pages/smartfacture/CreateInvoicePage';
import ScannerPage from './pages/smartfacture/ScannerPage';
import ClientsPage from './pages/smartfacture/ClientsPage';
import QuotesPage from './pages/smartfacture/QuotesPage';
import InboxPage from './pages/smartfacture/InboxPage';
import AccountingPage from './pages/smartfacture/AccountingPage';
import RecurringPage from './pages/smartfacture/RecurringPage';
import RemindersPage from './pages/smartfacture/RemindersPage';
import DashboardPremiumPage from './pages/smartfacture/DashboardPremiumPage';
import ExportPage from './pages/smartfacture/ExportPage';
import URSSAFPage from './pages/smartfacture/URSSAFPage';
import BankReconciliationPage from './pages/smartfacture/BankReconciliationPage';
import SimulatorAnalyticsPage from './pages/smartfacture/SimulatorAnalyticsPage';
import LocalModDashboard from './pages/localmod/LocalModDashboard';
import BusinessMetricsPage from './pages/BusinessMetricsPage';
import ProfilePage from './pages/profile/ProfilePage';
import SecurityPage from './pages/profile/SecurityPage';
import APIKeysPage from './pages/APIKeysPage';
import WebhooksPage from './pages/WebhooksPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

function AppContent() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePageSEO />} />
      <Route path="/comparaison" element={<ComparaisonPage />} />
      <Route path="/qui-sommes-nous" element={<QuiSommesNousPage />} />
      <Route path="/guide" element={<Navigate to="/guide-efacturation" replace />} />
      <Route path="/guide-efacturation" element={<GuideDownloadPage />} />
      <Route path="/simulateur" element={<SimulateurPage />} />
      <Route path="/partenaires" element={<PartenaireComptablePage />} />
      <Route path="/tutorial" element={<TutorialPage />} />
      <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
      <Route path="/dashboard/folders" element={<FoldersPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/partenaires/dashboard" element={<PartnerDashboardPage />} />
      <Route path="/optitrajet-demo" element={<OptiTrajetDemo />} />
      
      {/* Landing Pages Google Ads */}
      <Route path="/landing/facturation-2026" element={<LandingFacturation2026 />} />
      <Route path="/landing/freelance" element={<LandingFreelance />} />
      <Route path="/landing/impayes" element={<LandingImpayes />} />
      
      {/* Outils gratuits */}
      <Route path="/outils/facture-gratuite" element={<OutilFactureGratuit />} />
      
      {/* Early Adopter */}
      <Route path="/early-adopter" element={<EarlyAdopterPage />} />
      
      {/* Team Invitation */}
      <Route path="/join" element={<JoinTeamPage />} />
      
      {/* Pages de comparaison SEO */}
      <Route path="/comparaison/smartfacture-vs-indy" element={<SmartFactureVsIndy />} />
      <Route path="/comparaison/smartfacture-vs-freebe" element={<SmartFactureVsFreebe />} />
      <Route path="/comparaison/smartfacture-vs-tiime" element={<SmartFactureVsTiime />} />

      {/* Programmatic SEO — auto-generated pages from data files */}
      <Route path="/comparaison/:fullSlug" element={<ComparaisonProgPage />} />
      <Route path="/facturation-electronique/:slug" element={<CityPage />} />
      <Route path="/factur-x/:slug" element={<SecteurPage />} />
      <Route path="/calculateur-conformite-2026" element={<CalculateurConformitePage />} />
      
      {/* Pages Solutions */}
      <Route path="/solutions/freelance" element={<SolutionFreelance />} />
      <Route path="/solutions/auto-entrepreneur" element={<SolutionAutoEntrepreneur />} />
      <Route path="/solutions/artisan" element={<SolutionArtisan />} />
      <Route path="/solutions/pme" element={<SolutionPME />} />
      
      {/* OptiTrajet Routes (Separate App) */}
      <Route path="/optitrajet/login" element={<OptiTrajetLogin />} />
      <Route path="/optitrajet/register" element={<OptiTrajetRegister />} />
      <Route path="/optitrajet/dashboard" element={<OptiTrajetDashboard />} />
      <Route path="/optitrajet/trips" element={<OptiTrajetTrips />} />
      <Route path="/optitrajet/trips/new" element={<OptiTrajetNewTrip />} />
      <Route path="/optitrajet/expenses" element={<OptiTrajetExpenses />} />
      <Route path="/optitrajet/expenses/new" element={<OptiTrajetNewExpense />} />
      <Route path="/optitrajet/expenses/scan" element={<OptiTrajetScanExpense />} />
      <Route path="/optitrajet/vehicles" element={<OptiTrajetVehicles />} />
      <Route path="/optitrajet/reports" element={<OptiTrajetReports />} />
      <Route path="/optitrajet/settings" element={<OptiTrajetSettings />} />
      
      {/* Public Share Route (no auth required) */}
      <Route path="/share/:token" element={<SharedInvoicePage />} />
      
      <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
      <Route path="/cgu" element={<CGUPage />} />
      <Route path="/confidentialite" element={<ConfidentialitePage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Onboarding Route (protected) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="smartfacture" element={<SmartFactureDashboard />} />
        <Route path="smartfacture/create" element={<CreateInvoicePage />} />
        <Route path="smartfacture/scan" element={<ScannerPage />} />
        <Route path="smartfacture/clients" element={<ClientsPage />} />
        <Route path="smartfacture/devis" element={<QuotesPage />} />
        <Route path="smartfacture/inbox" element={<InboxPage />} />
        <Route path="smartfacture/comptabilite" element={<AccountingPage />} />
        <Route path="smartfacture/recurring" element={<RecurringPage />} />
        <Route path="smartfacture/relances" element={<RemindersPage />} />
        <Route path="smartfacture/dashboard-premium" element={<DashboardPremiumPage />} />
        <Route path="smartfacture/export" element={<ExportPage />} />
        <Route path="smartfacture/urssaf" element={<URSSAFPage />} />
        <Route path="smartfacture/banque" element={<BankReconciliationPage />} />
        <Route path="smartfacture/analytics" element={<SimulatorAnalyticsPage />} />
        <Route path="smartfacture/support" element={<SupportPage />} />
        <Route path="smartfacture/team" element={<TeamPage />} />
        <Route path="smartfacture/templates" element={<InvoiceTemplatesPage />} />
        <Route path="smartfacture/clients/import" element={<ClientImportPage />} />
        <Route path="smartfacture/ppf" element={<PPFPage />} />
        <Route path="smartfacture/qonto" element={<QontoIntegrationPage />} />
        <Route path="smartfacture/mobile-app" element={<MobileAppPage />} />
        <Route path="localmod" element={<LocalModDashboard />} />
        <Route path="metrics" element={<BusinessMetricsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="settings" element={<ProfilePage />} />
        <Route path="api-keys" element={<APIKeysPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route path="subscription/success" element={<SubscriptionSuccessPage />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Affiche le Copilote IA uniquement quand l'utilisateur est connecté
function CopilotGate() {
  const { user } = useAuth();
  if (!user) return null;
  return <Copilot />;
}

function App() {
  // Initialize Google Analytics 4 and Google Tag Manager
  useEffect(() => {
    initializeGA();
    initializeGTM();
  }, []);

  return (
    <BrowserRouter>
      <HelmetProvider>
        <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <NotificationProvider>
              <UpgradeModalProvider>
                <ReformBanner />
                <SEOSchemas />
                <Analytics />
                <AppContent />
                <CopilotGate />
                <Toaster />
                <CookieBanner />
              </UpgradeModalProvider>
            </NotificationProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
}

export default App;
